import { formatFileSize } from '../../../shared/src/utils/formatters';
import type { AuditActor, AuditCategory, AuditDetailValue, AuditLogEntry } from '../api';

/**
 * How Admin > Audit Logs words what the API returns: event and detail labels,
 * the one-line "Item" summary, and the actor. The events and detail keys are
 * the vocabulary of lambda/app-api/audit-allowlist.ts (keep the two in sync);
 * an unknown one still renders, under its raw key.
 */

export const FAKTO_SUPPORT_LABEL = 'Fakto Support';

export const CATEGORY_LABELS: Record<AuditCategory, string> = {
  sign_in: 'Sign-in',
  apps: 'Apps',
  files: 'Files',
  folders: 'Folders',
  people: 'People',
  groups: 'Groups',
  permissions: 'Access',
  projects: 'Projects',
  exports: 'Exports',
};

const EVENT_LABELS: Record<string, string> = {
  'user.login': 'Signed in',
  'user.login_failed': 'Failed sign-in',
  'user.logout': 'Signed out',
  'user.password_set': 'Set password',
  'user.password_change': 'Changed password',
  'user.password_reset_requested': 'Requested password reset',
  'user.password_reset_completed': 'Reset password',
  'app.open': 'Opened app',
  'file.upload': 'Uploaded file',
  'file.delete': 'Deleted file',
  'file.download': 'Downloaded file',
  'folder.download': 'Downloaded folder',
  'folder.create': 'Created folder',
  'folder.rename': 'Renamed folder',
  'folder.move': 'Moved folder',
  'folder.description_update': 'Edited folder description',
  'folder.delete': 'Deleted folder',
  'folder.move_project': 'Moved folder to project',
  'user.invite': 'Invited user',
  'user.role_change': 'Changed role',
  'user.delete': 'Removed user',
  'user.invitation_resend': 'Resent invitation',
  'user.profile_update': 'Updated profile',
  'group.create': 'Created group',
  'group.update': 'Updated group',
  'group.delete': 'Deleted group',
  'permission.update': 'Changed access',
  'project.create': 'Created project',
  'project.rename': 'Renamed project',
  'project.delete': 'Deleted project',
  'audit.export': 'Exported audit logs',
};

const DETAIL_LABELS: Record<string, string> = {
  method: 'Method',
  identityProvider: 'Identity provider',
  reason: 'Reason',
  accountLocked: 'Account locked',
  app: 'App',
  file: 'File',
  path: 'Path',
  size: 'Size',
  originalName: 'Original name',
  folder: 'Folder',
  fileCount: 'Files',
  parentFolder: 'Parent folder',
  oldName: 'Previous name',
  newName: 'New name',
  fromFolder: 'From folder',
  toFolder: 'To folder',
  description: 'Description',
  fromProject: 'From project',
  toProject: 'To project',
  user: 'User',
  firstName: 'First name',
  lastName: 'Last name',
  role: 'Role',
  previousRole: 'Previous role',
  group: 'Group',
  change: 'Change',
  member: 'Member',
  memberCount: 'Members',
  permission: 'Access',
  name: 'Name',
  format: 'Format',
  scope: 'Scope',
  rowCount: 'Events',
  actions: 'Actions',
  users: 'Users',
  projects: 'Projects',
  search: 'Item contains',
  excludeFaktoSupport: 'Fakto Support activity',
  dateFrom: 'From',
  dateTo: 'To',
  timeZone: 'Time zone',
};

const VALUE_LABELS: Record<string, Record<string, string>> = {
  method: { password: 'Password', saml: 'SSO (SAML)', oidc: 'SSO (OIDC)', invitation: 'Invitation' },
  reason: { invalid_password: 'Wrong password' },
  role: { org_admin: 'Admin', user: 'Member' },
  previousRole: { org_admin: 'Admin', user: 'Member' },
  permission: { owner: 'Owner', viewer: 'Viewer', none: 'No access' },
  change: {
    member_added: 'Added member',
    member_removed: 'Removed member',
    members_replaced: 'Replaced members',
    renamed: 'Renamed',
  },
  format: { csv: 'CSV', xlsx: 'Excel' },
  scope: { all: 'All history', selection: 'Selected data' },
};

export function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

export function detailLabel(key: string): string {
  return DETAIL_LABELS[key] ?? key;
}

export function formatDetailValue(key: string, value: AuditDetailValue): string {
  if (key === 'actions' && Array.isArray(value)) return value.map(eventLabel).join(', ');
  if (Array.isArray(value)) return value.join(', ');
  if (key === 'excludeFaktoSupport') return value ? 'Excluded' : 'Included';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'size' && typeof value === 'number') return formatFileSize(value);
  if (typeof value === 'number') return value.toLocaleString('en-US');
  return VALUE_LABELS[key]?.[value] ?? value;
}

/** Every detail as a [label, text] pair, in the order the API sent them. */
export function detailEntries(entry: AuditLogEntry): [string, string][] {
  return Object.entries(entry.details).map(([key, value]) => [detailLabel(key), formatDetailValue(key, value)]);
}

export function actorLabel(actor: AuditActor): string {
  if (actor.type === 'user') return actor.email;
  return actor.type === 'fakto_support' ? FAKTO_SUPPORT_LABEL : 'Deleted user';
}

export function projectLabel(project: AuditLogEntry['project']): string {
  if (!project) return '';
  return project.name ?? 'Deleted project';
}

/** The table's "Item" column: what the event acted on, in a line. */
export function summarize(entry: AuditLogEntry): string {
  const d = entry.details;
  const text = (key: string) => (d[key] === undefined ? '' : formatDetailValue(key, d[key]));
  const arrow = (from: string, to: string) => (from && to ? `${from} → ${to}` : to || from);
  const scoped = (head: string, tail: string) => (head && tail ? `${head}: ${tail}` : head || tail);

  switch (entry.event) {
    case 'user.login':
      // Password sign-ins are the norm; name the identity provider of an SSO one
      return d.method === 'password' ? '' : text('identityProvider') || text('method');
    case 'user.login_failed':
      return [text('reason'), d.accountLocked ? 'account locked' : ''].filter(Boolean).join(', ');
    case 'app.open':
      return text('app');
    case 'file.upload':
    case 'file.delete':
    case 'file.download':
      return text('path') || text('file');
    case 'folder.download':
      return d.fileCount !== undefined ? `${text('folder')} (${text('fileCount')} files)` : text('folder');
    case 'folder.rename':
    case 'project.rename':
      return arrow(text('oldName'), text('newName'));
    case 'folder.move':
      return scoped(text('folder'), arrow(text('fromFolder'), text('toFolder')));
    case 'folder.move_project':
      return scoped(text('folder'), arrow(text('fromProject'), text('toProject')));
    case 'user.role_change':
      return scoped(text('user'), arrow(text('previousRole'), text('role')));
    case 'user.profile_update':
      return [text('firstName'), text('lastName')].filter(Boolean).join(' ');
    case 'group.update':
      switch (d.change) {
        case 'member_added':
        case 'member_removed':
          return scoped(text('group'), `${text('change').toLowerCase()} ${text('member')}`.trim());
        case 'members_replaced':
          return scoped(text('group'), `${text('memberCount')} members`);
        default:
          return arrow(text('oldName'), text('group'));
      }
    case 'permission.update':
      return scoped(text('user') || text('group'), text('permission'));
    case 'audit.export': {
      const count = d.rowCount !== undefined ? `${text('rowCount')} events` : '';
      return [text('format'), text('scope'), count].filter(Boolean).join(' · ');
    }
    default:
      return text('user') || text('folder') || text('group') || text('name') || text('description');
  }
}
